import api from './client';

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

export const setupAPI = {
  status: () => api.get('/setup/status'),
  bootstrap: (data) => api.post('/setup/bootstrap', data),
};

export const userAPI = {
  getAll: () => api.get('/users'),
  getDropdown: () => api.get('/users/dropdown'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  linkToCustomer: (userId, customerId) => api.put(`/users/${userId}/link-customer/${customerId}`),
  linkToCarrier: (userId, carrierId) => api.put(`/users/${userId}/link-carrier/${carrierId}`),
  // User Preferences
  getPreferences: () => api.get('/users/preferences'),
  updatePreferences: (preferences) => api.put('/users/preferences', preferences),
  updatePreferenceKey: (key, value) => api.put(`/users/preferences/${key}`, value),
};

export const auditAPI = {
  getByEntity: (entityType, entityId) => api.get(`/audit/${entityType}/${entityId}`),
};

export const customerAPI = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id, reason = '') => api.delete(`/customers/${id}?reason=${encodeURIComponent(reason)}`),
};

export const facilityAPI = {
  getAll: () => api.get('/facilities'),
  getById: (id) => api.get(`/facilities/${id}`),
  create: (data) => api.post('/facilities', data),
  update: (id, data) => api.put(`/facilities/${id}`, data),
  delete: (id, reason = '') => api.delete(`/facilities/${id}?reason=${encodeURIComponent(reason)}`),
};

export const carrierAPI = {
  getAll: () => api.get('/carriers'),
  getById: (id) => api.get(`/carriers/${id}`),
  create: (data) => api.post('/carriers', data),
  update: (id, data) => api.put(`/carriers/${id}`, data),
  delete: (id, reason = '') => api.delete(`/carriers/${id}?reason=${encodeURIComponent(reason)}`),
  addDriver: (carrierId, driver) => api.post(`/carriers/${carrierId}/drivers`, driver),
};

export const crossBorderAPI = {
  meta: () => api.get('/cross-border/locations'),
  reviewQueue: () => api.get('/cross-border/review-queue'),
  recordEvent: (loadId, body) => api.post(`/loads/${loadId}/cross-border/events`, body),
  correctEvent: (loadId, eventId, body) => api.patch(`/loads/${loadId}/cross-border/events/${eventId}`, body),
  addCrossing: (loadId, body) => api.post(`/loads/${loadId}/cross-border/crossings`, body),
  setOverride: (loadId, body) => api.post(`/loads/${loadId}/cross-border/override`, body),
  clearOverride: (loadId, reason) => api.delete(`/loads/${loadId}/cross-border/override`, { params: { reason } }),
  setDocState: (loadId, key, body) => api.post(`/loads/${loadId}/cross-border/documents/${key}`, body),
  dispatch: (loadId, body = {}) => api.post(`/loads/${loadId}/dispatch`, body),
};

export const loadAPI = {
  getAll: (userIds = [], filters = {}) => {
    const params = new URLSearchParams();
    if (userIds && userIds.length > 0) {
      params.append('user_ids', userIds.join(','));
    }
    if (filters.team_user_id) params.append('team_user_id', filters.team_user_id);
    if (filters.customer_id) params.append('customer_id', filters.customer_id);
    const queryString = params.toString();
    return api.get(`/loads${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => api.get(`/loads/${id}`),
  quickSearch: (q) => api.get(`/loads/quick-search?q=${encodeURIComponent(q)}`),
  create: (data) => api.post('/loads', data),
  createBlank: () => api.post('/loads/blank'),
  update: (id, data) => api.put(`/loads/${id}`, data),
  delete: (id, reason = '') => api.delete(`/loads/${id}?reason=${encodeURIComponent(reason)}`),
};

export const accessorialAPI = {
  create: (data) => api.post('/accessorials', data),
  getByLoad: (loadId) => api.get(`/accessorials/load/${loadId}`),
};

export const documentAPI = {
  create: (data) => api.post('/documents', data),
  getByEntity: (entityType, entityId) => api.get(`/documents/${entityType}/${entityId}`),
  delete: (id) => api.delete(`/documents/${id}`),
  upload: (file, entityType, entityId, documentType, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    if (entityType) formData.append('entity_type', entityType);
    if (entityId) formData.append('entity_id', entityId);
    if (documentType) formData.append('document_type', documentType);
    if (notes) formData.append('notes', notes);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFileUrl: (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`,
};

export const interactionAPI = {
  create: (data) => api.post('/interactions', data),
  getByEntity: (entityType, entityId) => api.get(`/interactions/${entityType}/${entityId}`),
};

export const searchAPI = {
  search: (q, limit = 8) => api.get(`/search?q=${encodeURIComponent(q)}&limit=${limit}`),
};

export const teamAPI = {
  directory: () => api.get('/team/directory'),
};

export const checkCallAPI = {
  queue: () => api.get('/check-calls/queue'),
  create: (data) => api.post('/check-calls', data),
  history: (loadId, stopIndex, taskKey) => api.get(`/check-calls/${loadId}?${[stopIndex !== undefined && stopIndex !== null ? `stop_index=${stopIndex}` : null, taskKey ? `task_key=${encodeURIComponent(taskKey)}` : null].filter(Boolean).join('&')}`),
  settings: () => api.get('/check-calls/settings'),
  saveSettings: (data) => api.put('/check-calls/settings', data),
  missedReport: (date) => api.get(`/check-calls/report/missed${date ? `?date=${date}` : ''}`),
};

export const laneAPI = {
  create: (data) => api.post('/lanes', data),
  getByCustomer: (customerId) => api.get(`/lanes/customer/${customerId}`),
  update: (id, data) => api.put(`/lanes/${id}`, data),
  delete: (id) => api.delete(`/lanes/${id}`),
};

export const statsAPI = {
  getDashboard: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.operator_id) params.append('filter_operator_id', filters.operator_id);
    if (filters.customer_id) params.append('filter_customer_id', filters.customer_id);
    if (filters.margin_week_offset) params.append('margin_week_offset', filters.margin_week_offset);
    const qs = params.toString();
    return api.get(`/stats/dashboard${qs ? `?${qs}` : ''}`);
  },
  getLoadsByStatus: () => api.get('/stats/loads-by-status'),
  getCarrierPerformance: () => api.get('/stats/carrier-performance'),
  getCommission: (weekOffset = 0, periodNumber = null) => {
    let params = `?week_offset=${weekOffset}`;
    if (periodNumber) params += `&period_number=${periodNumber}`;
    return api.get(`/stats/commission${params}`);
  },
  getUncoveredLoads: (startDate, endDate) => {
    return api.get(`/stats/uncovered-loads?start_date=${startDate}&end_date=${endDate}`);
  },
};

export const reloadAPI = {
  getMapData: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.zipCode) params.append('zip_code', filters.zipCode);
    if (filters.equipment) params.append('equipment', filters.equipment);
    return api.get(`/loads/reload-map?${params.toString()}`);
  },
};

export const fuelPriceAPI = {
  fetchPrices: () => api.post('/fuel-prices/fetch'),
  getCurrent: () => api.get('/fuel-prices/current'),
  getHistory: (months = 6) => api.get(`/fuel-prices/history?months=${months}`),
  lookupFSC: (customerId, fuelPrice = null, shipDate = null) => {
    if (!customerId) return Promise.resolve({ data: { fsc_value: null, message: 'no customer_id' } });
    const params = new URLSearchParams({ customer_id: customerId });
    if (fuelPrice) params.append('fuel_price', fuelPrice);
    if (shipDate) params.append('ship_date', shipDate);
    return api.post(`/fuel-prices/lookup-fsc?${params.toString()}`);
  },
};

export const customerFuelAPI = {
  getConfig: (customerId) => api.get(`/customers/${customerId}/fuel-surcharge`),
  updateConfig: (customerId, config) => api.put(`/customers/${customerId}/fuel-surcharge`, config),
  uploadTable: (customerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/customers/${customerId}/fuel-surcharge/upload-table`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  pasteTable: (customerId, text, name) =>
    api.post(`/customers/${customerId}/fuel-surcharge/paste-table`, { text, name }),
};


// Centralized Interactions API
export const interactionsAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.entityType) params.append('entity_type', filters.entityType);
    if (filters.interactionType) params.append('interaction_type', filters.interactionType);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit);
    return api.get(`/interactions/all?${params.toString()}`);
  },
  getByEntity: (entityType, entityId) => api.get(`/interactions/${entityType}/${entityId}`),
  create: (data) => api.post('/interactions', data),
};

// Workflows API
export const workflowAPI = {
  getTemplates: () => api.get('/workflows/templates'),
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.entityType) params.append('entity_type', filters.entityType);
    return api.get(`/workflows?${params.toString()}`);
  },
  getById: (id) => api.get(`/workflows/${id}`),
  create: (data) => api.post('/workflows', data),
  updateStep: (workflowId, stepId, completed) => 
    api.put(`/workflows/${workflowId}/steps/${stepId}?completed=${completed}`),
  delete: (id) => api.delete(`/workflows/${id}`),
};

// Bulk Import APIs
export const importAPI = {
  downloadCarrierTemplate: () => api.get('/templates/carriers', { responseType: 'blob' }),
  downloadCustomerTemplate: () => api.get('/templates/customers', { responseType: 'blob' }),
  downloadFacilityTemplate: () => api.get('/templates/facilities', { responseType: 'blob' }),
  importCarriers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/carriers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importCustomers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/customers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importFacilities: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/facilities', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};
export const accessorialCodeAPI = {
  search: (q) => api.get('/accessorial-codes', { params: q ? { q } : {} }),
  create: (data) => api.post('/accessorial-codes', data),
  delete: (id) => api.delete(`/accessorial-codes/${id}`),
};
